/**
 * Admin Dashboard UI Module
 * Unified tooltip, drawer, modal, and loading state management
 */

(function () {
  'use strict';

  // Conditional logging setup — delegate to core's isDev flag when available (single source of truth).
  // Local fallback covers the unlikely case this module loads before core.js.
  const isDev =
    window.AdminDashboardCore?.isDev ??
    (window.location.hostname === 'localhost' || window.location.hostname.includes('.dev'));
  const warn = isDev ? console.warn.bind(console) : () => {};

  window.AdminDashboardUI = {
    // Reference to core module
    core: null,

    /**
     * Initialize UI module
     */
    init(coreModule) {
      this.core = coreModule;
    },

    // === LOADING STATES ===

    /**
     * Show loading spinner in container
     * Unified loading state used in 30+ functions
     *
     * @param {string|HTMLElement} containerId - Container element or its DOM id
     * @param {string} [message='Loading...'] - Loading message text
     * @returns {HTMLElement|null} The container element, or null if not found
     */
    showLoading(containerId, message = 'Loading...') {
      const container =
        typeof containerId === 'string' ? document.getElementById(containerId) : containerId;

      if (!container) {
        warn(`Container ${containerId} not found`);
        return null;
      }
      // Coerce message to string to prevent textContent receiving non-string types
      const safeMessage = message != null ? String(message) : 'Loading...';

      // Store original content if not already stored
      if (!container.dataset.originalContent) {
        container.dataset.originalContent = container.innerHTML;
      }

      // Build loading UI safely without innerHTML
      const loadingContainer = document.createElement('div');
      loadingContainer.className = 'loading-container';
      loadingContainer.setAttribute('aria-busy', 'true');
      loadingContainer.setAttribute('aria-label', safeMessage);
      loadingContainer.style.cssText =
        'display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 40px;';

      const spinnerDiv = document.createElement('div');
      spinnerDiv.className = 'loading-spinner large';

      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('class', 'animate-spin h-8 w-8');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('focusable', 'false');
      svg.style.cssText = 'width: 32px; height: 32px; color: rgba(255,255,255,0.8);';

      const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      circle.setAttribute('class', 'opacity-25');
      circle.setAttribute('cx', '12');
      circle.setAttribute('cy', '12');
      circle.setAttribute('r', '10');
      circle.setAttribute('stroke', 'currentColor');
      circle.setAttribute('stroke-width', '4');
      circle.setAttribute('fill', 'none');

      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('class', 'opacity-75');
      path.setAttribute('fill', 'currentColor');
      path.setAttribute(
        'd',
        'M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
      );

      svg.appendChild(circle);
      svg.appendChild(path);
      spinnerDiv.appendChild(svg);

      const loadingText = document.createElement('div');
      loadingText.className = 'loading-text';
      loadingText.style.cssText =
        'margin-top: 12px; color: rgba(255,255,255,0.7); font-size: 14px;';
      loadingText.textContent = safeMessage;

      loadingContainer.appendChild(spinnerDiv);
      loadingContainer.appendChild(loadingText);
      container.replaceChildren(loadingContainer);

      return container;
    },

    /**
     * Clear loading state and restore original content or set new content
     */
    clearLoading(containerId, newContent = null) {
      const container =
        typeof containerId === 'string' ? document.getElementById(containerId) : containerId;

      if (!container) return false;

      // Remove aria-busy from the container itself (set by showLoading on the inner element,
      // but also clear it from the container in case it was set directly)
      container.removeAttribute('aria-busy');

      if (newContent) {
        // Treat newContent as HTML string - parse and insert safely
        const template = document.createElement('template');
        template.innerHTML = newContent;
        container.replaceChildren(...template.content.children);
      } else if (container.dataset.originalContent) {
        // Restore original content from dataset
        const template = document.createElement('template');
        template.innerHTML = container.dataset.originalContent;
        container.replaceChildren(...template.content.children);
        delete container.dataset.originalContent;
      } else {
        container.replaceChildren();
      }

      return true;
    },

    /**
     * Show error state in container
     *
     * @param {string|HTMLElement} containerId - Container element or its DOM id
     * @param {string} [message='An error occurred'] - User-facing error message
     * @param {string|null} [details=null] - Optional additional detail text
     * @returns {boolean} True if the container was found and updated
     */
    /**
     * Show an error state inside a container.
     * Optionally renders a "Retry" button when `onRetry` is provided, allowing
     * the user to re-trigger the load without refreshing the page.
     *
     * @param {string|HTMLElement} containerId - Container element or its DOM id
     * @param {string} [message='An error occurred'] - User-facing error message
     * @param {string|null} [details=null] - Optional additional detail text
     * @param {function|null} [onRetry=null] - Optional callback invoked when the user
     *   clicks the Retry button. When null, no button is rendered.
     * @returns {boolean} True if the container was found and updated
     */
    showError(containerId, message = 'An error occurred', details = null, onRetry = null) {
      const container =
        typeof containerId === 'string' ? document.getElementById(containerId) : containerId;

      if (!container) return false;

      // Coerce to string so non-string errors (Error objects, numbers) render correctly
      const safeMessage = message != null ? String(message) : 'An error occurred';
      const safeDetails = details != null ? String(details) : null;

      // Build error UI safely without innerHTML
      const errorContainer = document.createElement('div');
      errorContainer.className = 'error-container';
      errorContainer.setAttribute('role', 'alert');
      errorContainer.style.cssText =
        'display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 40px; color: rgba(255,255,255,0.7);';

      const errorIcon = document.createElement('div');
      errorIcon.className = 'error-icon';
      errorIcon.style.cssText = 'font-size: 48px; margin-bottom: 12px;';
      errorIcon.textContent = '⚠️';

      const errorMessage = document.createElement('div');
      errorMessage.className = 'error-message';
      errorMessage.style.cssText = 'font-size: 16px; font-weight: 600; margin-bottom: 8px;';
      errorMessage.textContent = safeMessage;

      errorContainer.appendChild(errorIcon);
      errorContainer.appendChild(errorMessage);

      if (safeDetails) {
        const errorDetails = document.createElement('div');
        errorDetails.className = 'error-details';
        errorDetails.style.cssText = 'font-size: 12px; opacity: 0.7;';
        errorDetails.textContent = safeDetails;
        errorContainer.appendChild(errorDetails);
      }

      // Render a Retry button with auto-countdown when a callback is provided.
      // The button uses pure DOM construction (no innerHTML) to satisfy the security hook.
      if (typeof onRetry === 'function') {
        const retryBtn = document.createElement('button');
        retryBtn.className = 'error-retry-btn admin-dash-retry-btn';
        retryBtn.style.cssText =
          'margin-top: 16px; padding: 8px 20px; background: rgba(255,255,255,0.15); ' +
          'color: rgba(255,255,255,0.9); border: 1px solid rgba(255,255,255,0.3); ' +
          'border-radius: 6px; cursor: pointer; font-size: 14px; font-weight: 500;';

        const COUNTDOWN_SECS = 5;
        let remaining = COUNTDOWN_SECS;
        let countdownTimer = null;

        const doRetry = () => {
          if (countdownTimer !== null) {
            clearInterval(countdownTimer);
            countdownTimer = null;
          }
          retryBtn.disabled = true;
          retryBtn.textContent = 'Retrying…';
          onRetry();
        };

        const tick = () => {
          remaining -= 1;
          if (remaining <= 0) {
            doRetry();
          } else {
            retryBtn.textContent = `Retry in ${remaining}s…`;
          }
        };

        retryBtn.textContent = `Retry in ${remaining}s…`;
        countdownTimer = setInterval(tick, 1000);

        retryBtn.addEventListener('click', doRetry);
        errorContainer.appendChild(retryBtn);
      }

      container.replaceChildren(errorContainer);
      return true;
    },

    // === SKELETON / EMPTY STATE HELPERS ===

    /**
     * Show a shimmer skeleton placeholder that matches the shape of the content
     * being loaded. Prevents layout jump when data arrives.
     *
     * @param {string|HTMLElement} containerId - Container element or its DOM id
     * @param {'stat'|'chart'|'table'|'list'} [type='stat'] - Skeleton shape variant
     * @returns {HTMLElement|null} The container element, or null if not found
     */
    showSkeleton(containerId, type = 'stat') {
      const container =
        typeof containerId === 'string' ? document.getElementById(containerId) : containerId;

      if (!container) {
        warn(`Container ${containerId} not found`);
        return null;
      }

      // Store original content if not already stored
      if (!container.dataset.originalContent) {
        container.dataset.originalContent = container.innerHTML;
      }

      const wrapper = document.createElement('div');
      wrapper.className = `admin-dash-skeleton admin-dash-skeleton--${type}`;
      wrapper.setAttribute('aria-label', 'Loading…');
      wrapper.setAttribute('aria-busy', 'true');

      if (type === 'stat') {
        // Stat card: a wide value line + a narrow meta line
        const valueLine = document.createElement('div');
        valueLine.className = 'admin-dash-skeleton__line admin-dash-skeleton__line--value';

        const metaLine = document.createElement('div');
        metaLine.className = 'admin-dash-skeleton__line admin-dash-skeleton__line--meta';

        wrapper.appendChild(valueLine);
        wrapper.appendChild(metaLine);
      } else if (type === 'chart') {
        // Chart: a tall rectangular block
        const chartBlock = document.createElement('div');
        chartBlock.className = 'admin-dash-skeleton__block admin-dash-skeleton__block--chart';
        wrapper.appendChild(chartBlock);
      } else if (type === 'table') {
        // Table: three rows of varying widths
        for (let i = 0; i < 3; i++) {
          const row = document.createElement('div');
          row.className = `admin-dash-skeleton__line admin-dash-skeleton__line--row admin-dash-skeleton__line--row-${i + 1}`;
          wrapper.appendChild(row);
        }
      } else if (type === 'list') {
        // List: two shorter rows
        for (let i = 0; i < 2; i++) {
          const item = document.createElement('div');
          item.className = 'admin-dash-skeleton__line admin-dash-skeleton__line--list-item';
          wrapper.appendChild(item);
        }
      }

      container.replaceChildren(wrapper);
      return container;
    },

    /**
     * Show a descriptive empty-state message when an API returns 0 results.
     * Replaces raw "--" placeholders with a human-readable explanation.
     *
     * @param {string|HTMLElement} containerId - Container element or its DOM id
     * @param {string} [message='No data available'] - Primary empty-state message
     * @param {string|null} [hint=null] - Optional secondary hint text (e.g. "Try a different date range")
     * @returns {boolean} True if the container was found and updated
     */
    showEmptyState(containerId, message = 'No data available', hint = null) {
      const container =
        typeof containerId === 'string' ? document.getElementById(containerId) : containerId;

      if (!container) return false;

      const safeMessage = message != null ? String(message) : 'No data available';
      const safeHint = hint != null ? String(hint) : null;

      const wrapper = document.createElement('div');
      wrapper.className = 'admin-dash-empty-state';
      wrapper.setAttribute('aria-label', safeMessage);

      const icon = document.createElement('div');
      icon.className = 'admin-dash-empty-state__icon';
      icon.textContent = '—'; // em-dash as minimal icon

      const msg = document.createElement('div');
      msg.className = 'admin-dash-empty-state__message';
      msg.textContent = safeMessage;

      wrapper.appendChild(icon);
      wrapper.appendChild(msg);

      if (safeHint) {
        const hintEl = document.createElement('div');
        hintEl.className = 'admin-dash-empty-state__hint';
        hintEl.textContent = safeHint;
        wrapper.appendChild(hintEl);
      }

      container.replaceChildren(wrapper);
      return true;
    },

    // === TOOLTIP SYSTEM ===

    /**
     * Show tooltip at position
     * Unified tooltip system replacing 6 separate implementations
     */
    showTooltip(tooltipId, content, options = {}) {
      // Hide existing tooltip
      this.hideTooltip(tooltipId);

      // Create tooltip element if it doesn't exist
      let tooltip = document.getElementById(tooltipId);
      if (!tooltip) {
        tooltip = document.createElement('div');
        tooltip.id = tooltipId;
        tooltip.className = 'unified-tooltip';
        document.body.appendChild(tooltip);
      }

      // Set content safely
      if (typeof content === 'string' && content.includes('<')) {
        // Content is HTML - parse safely
        const template = document.createElement('template');
        template.innerHTML = content;
        tooltip.replaceChildren(...template.content.children);
      } else {
        // Plain text
        tooltip.textContent = content;
      }
      tooltip.style.display = 'block';
      tooltip.style.position = 'absolute';
      tooltip.style.zIndex = '1000';

      // Apply custom styles
      if (options.className) {
        tooltip.className += ` ${options.className}`;
      }

      // Position tooltip
      if (options.triggerElement) {
        this.positionTooltip(tooltip, options.triggerElement, options.position || 'bottom');
      } else if (options.x !== undefined && options.y !== undefined) {
        tooltip.style.left = `${options.x}px`;
        tooltip.style.top = `${options.y}px`;
      }

      return tooltip;
    },

    /**
     * Position tooltip relative to trigger element.
     *
     * @param {HTMLElement} tooltip - The tooltip DOM element to position
     * @param {HTMLElement} triggerElement - The element the tooltip should anchor to
     * @param {'bottom'|'top'|'left'|'right'} [position='bottom'] - Preferred placement
     */
    positionTooltip(tooltip, triggerElement, position = 'bottom') {
      // Guard: triggerElement must be a real DOM element with getBoundingClientRect
      if (!triggerElement || typeof triggerElement.getBoundingClientRect !== 'function') {
        return;
      }
      const triggerRect = triggerElement.getBoundingClientRect();
      const tooltipRect = tooltip.getBoundingClientRect();
      const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
      const scrollY = window.pageYOffset || document.documentElement.scrollTop;

      const positions = {
        bottom: {
          left: triggerRect.left + scrollX + triggerRect.width / 2 - tooltipRect.width / 2,
          top: triggerRect.bottom + scrollY + 8,
        },
        top: {
          left: triggerRect.left + scrollX + triggerRect.width / 2 - tooltipRect.width / 2,
          top: triggerRect.top + scrollY - tooltipRect.height - 8,
        },
        right: {
          left: triggerRect.right + scrollX + 8,
          top: triggerRect.top + scrollY + triggerRect.height / 2 - tooltipRect.height / 2,
        },
        left: {
          left: triggerRect.left + scrollX - tooltipRect.width - 8,
          top: triggerRect.top + scrollY + triggerRect.height / 2 - tooltipRect.height / 2,
        },
      };

      const pos = positions[position] || positions.bottom;

      // Keep tooltip within viewport
      const maxX = window.innerWidth + scrollX - tooltipRect.width - 16;
      const maxY = window.innerHeight + scrollY - tooltipRect.height - 16;

      tooltip.style.left = `${Math.max(16, Math.min(pos.left, maxX))}px`;
      tooltip.style.top = `${Math.max(16, Math.min(pos.top, maxY))}px`;
    },

    /**
     * Hide tooltip
     */
    hideTooltip(tooltipId) {
      const tooltip = document.getElementById(tooltipId);
      if (tooltip) {
        tooltip.style.display = 'none';
        // Optionally remove after hiding
        // tooltip.remove();
      }
    },

    /**
     * Remove tooltip from DOM
     */
    removeTooltip(tooltipId) {
      const tooltip = document.getElementById(tooltipId);
      if (tooltip) {
        tooltip.remove();
      }
    },

    // === DRAWER SYSTEM ===

    /**
     * Show drawer (mobile-optimized slide-up panel)
     * Unified drawer system for order details, tips, etc.
     */
    showDrawer(drawerId, content, _options = {}) {
      // Hide existing drawer
      this.hideDrawer(drawerId);

      // Create overlay
      const overlay = document.createElement('div');
      overlay.id = `${drawerId}-overlay`;
      overlay.className = 'drawer-overlay';
      overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.6);
      z-index: 999;
      opacity: 0;
      transition: opacity 0.3s ease;
    `;

      // Create drawer
      const drawer = document.createElement('div');
      drawer.id = drawerId;
      drawer.className = 'unified-drawer';
      drawer.style.cssText = `
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      max-height: 85vh;
      background: linear-gradient(135deg, rgba(30, 30, 50, 0.98), rgba(20, 20, 35, 0.98));
      border-radius: 24px 24px 0 0;
      z-index: 1000;
      transform: translateY(100%);
      transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      display: flex;
      flex-direction: column;
      box-shadow: 0 -10px 40px rgba(0, 0, 0, 0.5);
    `;

      // Add drawer handle
      const handle = document.createElement('div');
      handle.className = 'drawer-handle';
      handle.style.cssText = `
      flex-shrink: 0;
      padding: 16px;
      cursor: pointer;
      display: flex;
      justify-content: center;
    `;
      // Create handle indicator safely
      const handleIndicator = document.createElement('div');
      handleIndicator.style.cssText =
        'width: 40px; height: 4px; background: rgba(255,255,255,0.3); border-radius: 2px;';
      handle.appendChild(handleIndicator);

      // Add content container
      const contentContainer = document.createElement('div');
      contentContainer.className = 'drawer-content';
      contentContainer.style.cssText = `
      flex: 1;
      min-height: 0;
      overflow-y: auto;
      overflow-x: hidden;
      padding: 0 20px 32px 20px;
      padding-bottom: max(32px, env(safe-area-inset-bottom, 32px));
    `;

      // Set content safely
      if (typeof content === 'string' && content.includes('<')) {
        const template = document.createElement('template');
        template.innerHTML = content;
        contentContainer.replaceChildren(...template.content.children);
      } else {
        contentContainer.textContent = content;
      }

      drawer.appendChild(handle);
      drawer.appendChild(contentContainer);

      // Append to body
      document.body.appendChild(overlay);
      document.body.appendChild(drawer);

      // Trigger animation
      requestAnimationFrame(() => {
        overlay.style.opacity = '1';
        drawer.style.transform = 'translateY(0)';
        drawer.classList.add('active');
        overlay.classList.add('active');
      });

      // Add close handlers
      const closeDrawer = () => {
        this.hideDrawer(drawerId);
      };

      overlay.addEventListener('click', closeDrawer);
      overlay.addEventListener(
        'touchstart',
        (e) => {
          e.preventDefault();
          e.stopPropagation();
          closeDrawer();
        },
        { passive: false }
      );

      handle.addEventListener('click', closeDrawer);
      handle.addEventListener(
        'touchstart',
        (e) => {
          e.preventDefault();
          e.stopPropagation();
          closeDrawer();
        },
        { passive: false }
      );

      return { overlay, drawer };
    },

    /**
     * Hide drawer with animation
     */
    hideDrawer(drawerId) {
      const overlay = document.getElementById(`${drawerId}-overlay`);
      const drawer = document.getElementById(drawerId);

      if (overlay && drawer) {
        // Set closing flag to prevent immediate reopening
        if (this.core?.state) {
          this.core.state.drawerClosing = true;
        }

        // Animate out
        overlay.style.opacity = '0';
        drawer.style.transform = 'translateY(100%)';
        overlay.classList.remove('active');
        drawer.classList.remove('active');

        // Remove after animation
        setTimeout(() => {
          overlay.remove();
          drawer.remove();

          // Clear closing flag
          setTimeout(() => {
            if (this.core?.state) {
              this.core.state.drawerClosing = false;
            }
          }, 100);
        }, 400);

        return true;
      }

      return false;
    },

    // === MODAL SYSTEM ===

    /**
     * Show modal dialog
     */
    showModal(modalId, content, options = {}) {
      // Hide existing modal
      this.hideModal(modalId);

      // Create overlay
      const overlay = document.createElement('div');
      overlay.id = `${modalId}-overlay`;
      overlay.className = 'modal-overlay';
      overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.7);
      z-index: 999;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      transition: opacity 0.3s ease;
    `;

      // Create modal
      const modal = document.createElement('div');
      modal.id = modalId;
      modal.className = 'unified-modal';
      modal.style.cssText = `
      background: linear-gradient(135deg, rgba(30, 30, 50, 0.98), rgba(20, 20, 35, 0.98));
      border-radius: 16px;
      max-width: ${options.maxWidth || '600px'};
      max-height: 90vh;
      width: 90%;
      overflow-y: auto;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
      transform: scale(0.9);
      transition: transform 0.3s ease;
      padding: 24px;
    `;

      // Set content safely
      if (typeof content === 'string' && content.includes('<')) {
        const template = document.createElement('template');
        template.innerHTML = content;
        modal.replaceChildren(...template.content.children);
      } else {
        modal.textContent = content;
      }

      overlay.appendChild(modal);
      document.body.appendChild(overlay);

      // Trigger animation
      requestAnimationFrame(() => {
        overlay.style.opacity = '1';
        modal.style.transform = 'scale(1)';
      });

      // Close on overlay click
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          this.hideModal(modalId);
        }
      });

      return { overlay, modal };
    },

    /**
     * Hide modal
     */
    hideModal(modalId) {
      const overlay = document.getElementById(`${modalId}-overlay`);

      if (overlay) {
        const modal = document.getElementById(modalId);

        // Animate out
        overlay.style.opacity = '0';
        if (modal) {
          modal.style.transform = 'scale(0.9)';
        }

        // Remove after animation
        setTimeout(() => {
          overlay.remove();
        }, 300);

        return true;
      }

      return false;
    },

    // === DOM UTILITIES ===

    /**
     * Safe HTML insertion — uses template element to block script execution.
     * Falls back to textContent for plain strings/numbers (no parsing overhead).
     */
    safeSetHTML(element, htmlString) {
      if (!element) return;
      const stringValue = String(htmlString);
      if (typeof htmlString === 'number' || !stringValue.includes('<')) {
        element.textContent = stringValue;
        return;
      }
      // template.innerHTML is safe: parsed but not evaluated; scripts never run
      const tmpl = document.createElement('template');
      tmpl.innerHTML = stringValue; // safe-via-template, not evaluated
      element.replaceChildren(...tmpl.content.childNodes);
    },

    /**
     * Update element content
     */
    updateElement(selector, content, options = {}) {
      const element = typeof selector === 'string' ? document.querySelector(selector) : selector;

      if (!element) return false;

      if (options.html) {
        this.safeSetHTML(element, content);
      } else {
        element.textContent = content;
      }

      if (options.className) {
        element.className = options.className;
      }

      if (options.style) {
        Object.assign(element.style, options.style);
      }

      return true;
    },

    /**
     * Toggle element visibility
     */
    toggleVisibility(selector, visible = null) {
      const element = typeof selector === 'string' ? document.querySelector(selector) : selector;

      if (!element) return false;

      if (visible === null) {
        // Toggle
        element.style.display = element.style.display === 'none' ? '' : 'none';
      } else {
        element.style.display = visible ? '' : 'none';
      }

      return true;
    },

    /**
     * Add CSS class
     */
    addClass(selector, className) {
      const element = typeof selector === 'string' ? document.querySelector(selector) : selector;

      if (element) {
        element.classList.add(className);
        return true;
      }
      return false;
    },

    /**
     * Remove CSS class
     */
    removeClass(selector, className) {
      const element = typeof selector === 'string' ? document.querySelector(selector) : selector;

      if (element) {
        element.classList.remove(className);
        return true;
      }
      return false;
    },

    /**
     * Toggle CSS class
     */
    toggleClass(selector, className) {
      const element = typeof selector === 'string' ? document.querySelector(selector) : selector;

      if (element) {
        element.classList.toggle(className);
        return true;
      }
      return false;
    },

    /**
     * Check if mobile device
     */
    isMobile() {
      return window.innerWidth < 769;
    },

    /**
     * Smooth scroll to element
     */
    scrollTo(selector, options = {}) {
      const element = typeof selector === 'string' ? document.querySelector(selector) : selector;

      if (element) {
        element.scrollIntoView({
          behavior: options.behavior || 'smooth',
          block: options.block || 'start',
          inline: options.inline || 'nearest',
        });
        return true;
      }
      return false;
    },

    // ---------------------------------------------------------------------------
    // Connection-state banners (iter 35)
    //
    // All four banner types share _createBanner() which builds the DOM using
    // CSS classes from admin-dashboard.css (iter 35 block).  Each banner:
    //   • uses `position: sticky; top: 0` — never covers content
    //   • has a dismiss (×) button that hides just that banner
    //   • clears itself on reconnect (hideOfflineBanner / hideSlowConnectionWarning)
    //   • is visually distinct from error states via colour and z-index
    // ---------------------------------------------------------------------------

    /**
     * Create (or re-use) a sticky connection banner.
     *
     * @param {object} opts
     * @param {string} opts.id             - Element id
     * @param {string} opts.role           - ARIA role ('status'|'alert')
     * @param {string} opts.ariaLive       - ARIA live region ('polite'|'assertive')
     * @param {string} opts.text           - Main message text
     * @param {string|null} [opts.actionLabel]  - Optional action button label
     * @param {Function|null} [opts.onAction]   - Handler for action button click
     * @param {boolean} [opts.dismissible=true] - Show dismiss (×) button
     * @returns {HTMLElement} The banner element
     */
    _createBanner(opts) {
      let banner = document.getElementById(opts.id);
      if (!banner) {
        banner = document.createElement('div');
        banner.id = opts.id;
        banner.className = 'admin-dash-conn-banner';
        banner.setAttribute('role', opts.role || 'status');
        banner.setAttribute('aria-live', opts.ariaLive || 'polite');

        const span = document.createElement('span');
        span.className = 'admin-dash-conn-banner__text';
        banner.appendChild(span);

        if (opts.actionLabel && typeof opts.onAction === 'function') {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'admin-dash-conn-banner__action';
          btn.textContent = opts.actionLabel;
          btn.addEventListener('click', opts.onAction);
          banner.appendChild(btn);
        }

        if (opts.dismissible !== false) {
          const dismiss = document.createElement('button');
          dismiss.type = 'button';
          dismiss.className = 'admin-dash-conn-banner__dismiss';
          dismiss.setAttribute('aria-label', 'Dismiss');
          dismiss.textContent = '×';
          dismiss.addEventListener('click', () => {
            banner.hidden = true;
          });
          banner.appendChild(dismiss);
        }

        const dashboard = document.getElementById('admin-dashboard');
        if (dashboard) {
          dashboard.insertBefore(banner, dashboard.firstChild);
        } else {
          document.body.insertBefore(banner, document.body.firstChild);
        }
      }

      // Update text content only (leave action/dismiss buttons intact)
      const span = banner.querySelector('.admin-dash-conn-banner__text');
      if (span) span.textContent = opts.text;

      banner.hidden = false;
      return banner;
    },

    /**
     * Show (or update) the persistent offline banner at the top of the dashboard.
     * Creates the element if it does not already exist.
     *
     * @param {string} [message] - Override the default banner text
     */
    showOfflineBanner(message) {
      const text = message || 'Offline — reconnecting…';
      this._createBanner({
        id: 'admin-dash-offline-banner',
        role: 'status',
        ariaLive: 'polite',
        text,
        dismissible: true,
      });
    },

    /**
     * Hide and remove the offline banner if present.
     * Called when the 'online' event fires — ensures no ghost banner remains.
     */
    hideOfflineBanner() {
      const banner = document.getElementById('admin-dash-offline-banner');
      if (banner) {
        banner.hidden = true;
      }
    },

    /**
     * Show (or update) the slow-connection advisory banner.
     * Non-urgent — styled amber rather than red.
     *
     * @param {string} [effectiveType] - e.g. '2g' or 'slow-2g'
     */
    showSlowConnectionWarning(effectiveType) {
      const label = effectiveType ? effectiveType.toUpperCase() : 'slow';
      const text = `Slow connection detected (${label}) — some charts may load slowly`;
      this._createBanner({
        id: 'admin-dash-slow-conn-banner',
        role: 'status',
        ariaLive: 'polite',
        text,
        dismissible: true,
      });
    },

    /**
     * Hide the slow-connection advisory banner.
     * Called when connection restores — ensures no ghost banner remains after reconnect.
     */
    hideSlowConnectionWarning() {
      const banner = document.getElementById('admin-dash-slow-conn-banner');
      if (banner) {
        banner.hidden = true;
      }
    },

    /**
     * Show the persistent connection-problem warning.
     * Displayed when 3+ consecutive timeouts have been recorded.
     * Remains visible until the next successful request.
     *
     * @param {string} [message] - Override the default banner text
     */
    showConnectionProblemWarning(message) {
      const text = message || 'Connection problems detected — switch to manual refresh';
      this._createBanner({
        id: 'admin-dash-conn-problem-banner',
        role: 'alert',
        ariaLive: 'assertive',
        text,
        dismissible: true,
      });
    },

    /**
     * Hide and remove the connection-problem banner.
     * Called when a successful request resets connectionHealth.
     */
    hideConnectionProblemWarning() {
      const banner = document.getElementById('admin-dash-conn-problem-banner');
      if (banner) {
        banner.hidden = true;
      }
    },

    // ---------------------------------------------------------------------------
    // Save Data mode (iter 35)
    // ---------------------------------------------------------------------------

    /**
     * Show the Save Data advisory banner when the user is on a metered/very slow
     * connection (2g, slow-2g, or navigator.connection.saveData === true).
     *
     * Instead of auto-loading all charts, the banner offers an explicit
     * "Load charts anyway" button so the user can opt in when data costs matter.
     *
     * @param {Function} [onLoadAnyway] - Callback to execute when the user clicks
     *   "Load charts anyway".  Receives no arguments.
     */
    showSaveDataBanner(onLoadAnyway) {
      const text = 'Save Data mode — charts paused to reduce data usage';
      this._createBanner({
        id: 'admin-dash-save-data-banner',
        role: 'status',
        ariaLive: 'polite',
        text,
        actionLabel: 'Load charts anyway',
        onAction:
          typeof onLoadAnyway === 'function'
            ? () => {
                this.hideSaveDataBanner();
                onLoadAnyway();
              }
            : null,
        dismissible: true,
      });
    },

    /**
     * Hide the Save Data banner.
     */
    hideSaveDataBanner() {
      const banner = document.getElementById('admin-dash-save-data-banner');
      if (banner) {
        banner.hidden = true;
      }
    },

    /**
     * Detect whether Save Data mode should be active.
     * Returns true when navigator.connection.saveData is set OR effectiveType
     * is '2g' or 'slow-2g'.
     *
     * @returns {boolean}
     */
    isSaveDataMode() {
      const conn = navigator && navigator.connection;
      if (!conn) return false;
      if (conn.saveData) return true;
      const et = (conn.effectiveType || '').toLowerCase();
      return et === '2g' || et === 'slow-2g';
    },

    /**
     * Hide ALL connection-state banners in one call.
     * Used after a successful reconnect to ensure no ghost banners remain.
     */
    hideAllConnectionBanners() {
      [
        'admin-dash-offline-banner',
        'admin-dash-slow-conn-banner',
        'admin-dash-conn-problem-banner',
        'admin-dash-save-data-banner',
      ].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.hidden = true;
      });
    },
  };
})();
